import { Capacitor } from "@capacitor/core";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import { downloadBlob } from "@/utils/fileUtils";
import { toast } from "sonner";

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read blob"));
        return;
      }
      resolve(result.includes(",") ? result.split(",")[1]! : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

function safeFileName(name: string): string {
  return name.replace(/[/\\?%*:|"<>]/g, "_");
}

function mimeFromFileName(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "ics":
      return "text/calendar";
    case "pdf":
      return "application/pdf";
    case "csv":
      return "text/csv";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "txt":
      return "text/plain";
    default:
      return "application/octet-stream";
  }
}

export type SaveBlobDocumentOptions = {
  /** Open the file with a system "Open with" chooser after save. Default: true on native. */
  share?: boolean;
  open?: boolean;
  /**
   * When true, open with the default app for this type (e.g. Calendar for .ics on iOS)
   * instead of the options/share-like menu. Default: false.
   */
  openWithDefault?: boolean;
  /** Toast after save. Default: true when the file was not opened. */
  toast?: boolean;
  shareTitle?: string;
  shareDialogTitle?: string;
};

async function offerNativeOpen(
  uri: string,
  fileName: string,
  openWithDefault = false,
): Promise<boolean> {
  try {
    const { FileOpener } = await import("@capacitor-community/file-opener");
    await FileOpener.open({
      filePath: uri,
      contentType: mimeFromFileName(fileName),
      openWithDefault,
    });
    return true;
  } catch (e) {
    console.warn("Native file open failed", e);
    return false;
  }
}

function shouldOfferOpen(options: SaveBlobDocumentOptions): boolean {
  if (typeof options.open === "boolean") return options.open;
  if (typeof options.share === "boolean") return options.share;
  return true;
}

function shouldToastAfterSave(
  options: SaveBlobDocumentOptions,
  opened: boolean,
): boolean {
  if (typeof options.toast === "boolean") return options.toast;
  return !opened;
}

function toastNativeSaved(platform: string): void {
  if (platform === "ios") {
    toast.success(
      "Fil sparad. Hitta den i Filer → På min iPhone → Bilenia → Download",
    );
    return;
  }
  toast.success("Fil sparad i Nedladdningar");
}

/** Save any blob — browser download on web; Documents/Download + open-with on native. */
export async function saveBlobDocument(
  blob: Blob,
  fileName: string,
  options: SaveBlobDocumentOptions = {},
): Promise<void> {
  const safeName = safeFileName(fileName || "document");

  if (!isNativeCapacitorApp()) {
    downloadBlob(blob, safeName);
    return;
  }

  const base64 = await blobToBase64(blob);
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const platform = Capacitor.getPlatform();
  const downloadPath = `Download/${safeName}`;

  const finish = async (uri: string | undefined, openOverride?: boolean) => {
    let opened = false;
    const offerOpen =
      typeof openOverride === "boolean"
        ? openOverride
        : shouldOfferOpen(options);
    if (uri && offerOpen) {
      opened = await offerNativeOpen(
        uri,
        safeName,
        options.openWithDefault === true,
      );
    }
    if (shouldToastAfterSave(options, opened)) {
      toastNativeSaved(platform);
    }
  };

  try {
    const written = await Filesystem.writeFile({
      path: downloadPath,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });
    await finish(written.uri);
    return;
  } catch (e) {
    console.warn("Documents save failed, trying legacy ExternalStorage", e);
  }

  try {
    const written = await Filesystem.writeFile({
      path: downloadPath,
      data: base64,
      directory: Directory.ExternalStorage,
      recursive: true,
    });
    await finish(written.uri);
    return;
  } catch (e) {
    console.warn("ExternalStorage save failed, trying open from cache", e);
  }

  try {
    const written = await Filesystem.writeFile({
      path: safeName,
      data: base64,
      directory: Directory.Cache,
    });
    await finish(written.uri, true);
    return;
  } catch (e) {
    console.warn("Cache open fallback failed", e);
  }

  const written = await Filesystem.writeFile({
    path: safeName,
    data: base64,
    directory: Directory.Documents,
  });
  await finish(written.uri);
}
